export type DocumentTypeSource = "file" | "platform";

export interface DocumentTypeDeliveryTime {
  id?: number;
  document_type_id?: number;
  name: string;
  days: number;
  is_default?: boolean;
  is_standard?: boolean; // New field to explicitly mark Standard delivery type
  created_at?: Date;
  updated_at?: Date;
}

export interface DocumentTypeDeliverySchedule {
  from: string; // YYYY-MM-DD format
  name: string;
  is_default: boolean;
}

// Base interface for common fields
interface DocumentTypeBase {
  id: number;
  name: string;
  customer_types?: number[];
  generates_output_document?: boolean; // Whether this document type generates an output document when processed
  template_id?: number | null; // References a CMS template for output document generation
  created_at: Date;
  updated_at: Date;
}

// File-based document type with expiration settings
interface DocumentTypeFile extends DocumentTypeBase {
  source: "file";
  default_expiration_days: number | null;
  // Default expiration settings - Nth day of month/year
  default_expiration_day?: number; // Day of month (1-31)
  default_expiration_month?: number; // Month (1-12)
  default_expiration_year?: number; // Specific year, or null for annual
  default_expiration_type?: "days" | "date" | "annual"; // Type of expiration
  // New attributes for expired/missing document requests
  allow_expired_requests?: boolean; // Whether expired/missing documents can still be requested
  handling_time_days?: number; // Days needed to handle expired/missing document requests
}

// Platform-based document type with custom delivery times
interface DocumentTypePlatform extends DocumentTypeBase {
  source: "platform";
  accept_custom_form?: boolean; // Whether requestors can upload their own forms
  has_custom_delivery_times?: boolean; // Whether this document type uses custom delivery times
  is_updatable?: boolean; // Whether documents of this type can be updated
  min_update_processing_time?: number; // Minimum time required to process updates (in days)
  delivery_times?: DocumentTypeDeliveryTime[]; // Custom delivery time options
  delivery_schedule?: DocumentTypeDeliverySchedule[]; // Calculated delivery dates
}

// Discriminated union of document types
export type DocumentType = DocumentTypeFile | DocumentTypePlatform;

// Type guard functions
export function isPlatformDocumentType(
  docType: DocumentType
): docType is DocumentTypePlatform {
  return docType.source === "platform";
}

export function isFileDocumentType(
  docType: DocumentType
): docType is DocumentTypeFile {
  return docType.source === "file";
}

// Helper function to safely access delivery times
export function getDeliveryTimes(
  docType: DocumentType
): DocumentTypeDeliveryTime[] {
  return isPlatformDocumentType(docType) ? docType.delivery_times || [] : [];
}

// Helper function to safely access expiration settings
export function hasExpirationSettings(docType: DocumentType): boolean {
  return isFileDocumentType(docType);
}

// Helper function to get expiration type safely
export function getExpirationType(
  docType: DocumentType
): "days" | "date" | "annual" | undefined {
  return isFileDocumentType(docType)
    ? docType.default_expiration_type
    : undefined;
}

// Helper function to check if expired/missing documents can be requested
export function allowsExpiredRequests(docType: DocumentType): boolean {
  return isFileDocumentType(docType)
    ? docType.allow_expired_requests || false
    : false;
}

// Helper function to get handling time for expired/missing documents
export function getHandlingTimeDays(docType: DocumentType): number | null {
  return isFileDocumentType(docType)
    ? docType.handling_time_days || null
    : null;
}

export interface DocumentTypeSearchParams {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface DocumentTypeCustomerTypes {
  document_type_id: number;
  customer_type_id: number;
}

// Helper function to prepare delivery times for saving
export function prepareDeliveryTimesForSave(
  deliveryTimes: DocumentTypeDeliveryTime[]
): DocumentTypeDeliveryTime[] {
  if (!deliveryTimes || deliveryTimes.length === 0) {
    // If no delivery times provided, return empty array (Standard will be added by backend if needed)
    return [];
  }

  // Find the Standard option
  const standardOption = deliveryTimes.find((dt) => dt.name === "Standard");

  // If no Standard option exists, add it as the first option
  if (!standardOption) {
    return [
      { name: "Standard", days: 5, is_default: true },
      ...deliveryTimes.map((dt) => ({ ...dt, is_default: false })),
    ];
  }

  // Ensure Standard is marked as default and all others are not
  return deliveryTimes.map((dt) => ({
    ...dt,
    is_default: dt.name === "Standard",
  }));
}

// Helper function to validate delivery times
export function validateDeliveryTimes(
  deliveryTimes: DocumentTypeDeliveryTime[]
): boolean {
  if (!deliveryTimes || deliveryTimes.length === 0) return true;

  // Check if there's exactly one default option
  const defaultOptions = deliveryTimes.filter((dt) => dt.is_default);
  if (defaultOptions.length !== 1) return false;

  // Check if the default option is named "Standard"
  const standardOption = deliveryTimes.find((dt) => dt.name === "Standard");
  if (!standardOption || !standardOption.is_default) return false;

  return true;
}

// Helper function to ensure standard delivery time exists
export function ensureStandardDeliveryTime(
  deliveryTimes: DocumentTypeDeliveryTime[]
): DocumentTypeDeliveryTime[] {
  if (!deliveryTimes) return [];

  // Find the standard option if it exists
  const standardOption = deliveryTimes.find((dt) => dt.name === "Standard");

  // Filter out any non-standard options that might be marked as default
  const nonStandardOptions = deliveryTimes
    .filter((dt) => dt.name !== "Standard")
    .map((dt) => ({ ...dt, is_default: false, is_standard: false }));

  if (standardOption) {
    // Preserve the standard option with its original days but ensure it's default and marked as standard
    return [
      { ...standardOption, is_default: true, is_standard: true },
      ...nonStandardOptions,
    ];
  }

  // If no standard option exists, add it with default values
  return [
    { name: "Standard", days: 5, is_default: true, is_standard: true },
    ...nonStandardOptions,
  ];
}

// Helper function to get the standard delivery time
export function getStandardDeliveryTime(
  docType: DocumentType
): DocumentTypeDeliveryTime | null {
  if (!isPlatformDocumentType(docType) || !docType.delivery_times) return null;
  return docType.delivery_times.find((dt) => dt.name === "Standard") || null;
}

// Helper function to update delivery times while preserving the Standard option
export function updateDeliveryTimes(
  currentDeliveryTimes: DocumentTypeDeliveryTime[],
  newDeliveryTimes: DocumentTypeDeliveryTime[]
): DocumentTypeDeliveryTime[] {
  // Get the current standard option
  const currentStandard = currentDeliveryTimes.find(
    (dt) => dt.name === "Standard"
  );

  // Filter out the standard option from new delivery times
  const nonStandardNewTimes = newDeliveryTimes
    .filter((dt) => dt.name !== "Standard")
    .map((dt) => ({ ...dt, is_default: false, is_standard: false }));

  // If there's no current standard option, create one
  if (!currentStandard) {
    return [
      { name: "Standard", days: 5, is_default: true, is_standard: true },
      ...nonStandardNewTimes,
    ];
  }

  // Combine the preserved standard option with new non-standard options
  return [
    { ...currentStandard, is_default: true, is_standard: true },
    ...nonStandardNewTimes,
  ];
}

// Helper function to remove a delivery time while preserving the Standard option
export function removeDeliveryTime(
  deliveryTimes: DocumentTypeDeliveryTime[],
  timeToRemove: DocumentTypeDeliveryTime
): DocumentTypeDeliveryTime[] {
  // Don't allow removing the Standard option or any option marked as standard
  if (timeToRemove.name === "Standard" || timeToRemove.is_standard) {
    return deliveryTimes;
  }

  return deliveryTimes.filter((dt) => dt.id !== timeToRemove.id);
}

// Helper function to check if a delivery time can be edited
export function canEditDeliveryTime(
  deliveryTime: DocumentTypeDeliveryTime
): boolean {
  return !deliveryTime.is_standard;
}
